import React, { useState, useEffect, useRef } from 'react';

type AnimatedCounterProps = {
    value: number;
    duration?: number;
    formatFn?: (n: number) => string;
};

function animateValue(params: {
    from: number;
    to: number;
    duration: number;
    onUpdate: (value: number) => void;
    onComplete?: (value: number) => void;
}) {
    const { from, to, duration, onUpdate, onComplete } = params;

    let startTime: number;
    let rafId: number;

    const step = (t: number) => {
        if (!startTime) startTime = t;

        const progress = Math.min((t - startTime) / duration, 1);

        const current = from + (to - from) * progress;

        onUpdate(current);

        if (progress < 1) {
            rafId = requestAnimationFrame(step);
        } else {
            onComplete?.(to);
        }
    };

    rafId = requestAnimationFrame(step);

    return () => cancelAnimationFrame(rafId);
}

function AnimatedCounter({ value, duration = 400, formatFn }: AnimatedCounterProps) {
    const [display, setDisplay] = useState(value);
    const prev = useRef(value);
    const cleanupRef = useRef<null | (() => void)>(null);

    useEffect(() => {
        if (cleanupRef.current) {
            cleanupRef.current();
        }

        const from = prev.current;
        const to = value;

        cleanupRef.current = animateValue({
            from,
            to,
            duration,
            onUpdate: setDisplay,
            onComplete: (v) => {
                prev.current = v;
            },
        });

        return () => {
            cleanupRef.current?.();
            prev.current = value;
        };
    }, [value, duration]);

    if (formatFn) {
        return <>{formatFn(display)}</>;
    }

    return <>{display.toLocaleString()}</>;
}

export default AnimatedCounter;
